import './IdentityCard.css';
import { Avatar, Button, Card, CardContent, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import StatusIndicator from 'Components/StatusIndicator/StatusIndicator';
import { useAuth } from 'context/AuthContext';
import { useProfile } from 'context/ProfileContext';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCopy } from 'react-icons/fa6';
import { getAvatarUrl } from 'utils';
import { BRAND_LEFT, BRAND_RIGHT } from 'utils/constants';
import { STATUS } from 'utils/enums';

const IdentityWaves = ({ leftColor, rightColor, visible }: { leftColor: string; rightColor: string; visible: boolean }) => (
    <div className="identity-waves-container" style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease' }}>
        {/* Grain texture overlay */}
        <div className="identity-grain" />

        {/* Layer 1: Gradient sweep */}
        <div className="identity-gradient-sweep" style={{
            background: `linear-gradient(135deg, ${leftColor} 0%, transparent 50%, ${rightColor} 100%)`
        }} />

        {/* Layer 2: Filled organic waves */}
        <svg className="identity-waves-svg" viewBox="0 0 1400 400" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="waveGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={leftColor} stopOpacity="0.28" />
                    <stop offset="100%" stopColor={rightColor} stopOpacity="0.10" />
                </linearGradient>
                <linearGradient id="waveGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={rightColor} stopOpacity="0.08" />
                    <stop offset="100%" stopColor={leftColor} stopOpacity="0.22" />
                </linearGradient>
                <linearGradient id="waveGrad3" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={leftColor} stopOpacity="0.12" />
                    <stop offset="50%" stopColor={rightColor} stopOpacity="0.18" />
                    <stop offset="100%" stopColor={leftColor} stopOpacity="0.06" />
                </linearGradient>
            </defs>
            <path
                className="identity-wave identity-wave-1"
                d="M-100,220 C100,140 300,280 500,180 C700,80 900,260 1100,160 C1300,60 1400,180 1500,140 L1500,400 L-100,400 Z"
                fill="url(#waveGrad1)"
            />
            <path
                className="identity-wave identity-wave-2"
                d="M-100,280 C150,220 350,340 550,260 C750,180 900,320 1100,250 C1300,180 1400,280 1500,240 L1500,400 L-100,400 Z"
                fill="url(#waveGrad2)"
            />
            <path
                className="identity-wave identity-wave-3"
                d="M-100,330 C100,300 300,365 500,330 C700,295 850,360 1050,325 C1250,290 1350,350 1500,320 L1500,400 L-100,400 Z"
                fill="url(#waveGrad3)"
            />
        </svg>
    </div>
);

const IndentityCard = () => {
    const { t } = useTranslation();
    const { state, loading, deploy, pending } = useProfile();
    const { user: { userImage, username, deletedAt } } = useAuth();
    const isInDeletion = !!deletedAt;
    const [copytext, setCopyText] = useState("");
    const [openCopy, setOpenCopy] = useState(false);
    const [profileStatus, setProfileStatus] = useState<STATUS>(STATUS.DEFAULT);

    useEffect(() => {
        if (state.isPublished) {
            if (JSON.stringify(state.lastProfile) !== JSON.stringify(state.currentProfile)) {
              setProfileStatus(STATUS.DRAFT);
            } else {
              setProfileStatus(STATUS.PUBLISHED);
            }
        } else {
            setProfileStatus(STATUS.NOT_PUBLISHED)
        }
    }, [state]);

    const avatarSrc = useMemo(() => {
        if (!userImage?.fileName) return null;
        return getAvatarUrl(userImage);
    }, [userImage]);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(`https://linkerto.me/${username}`)
        setCopyText(t("profile.username.copied"));
    }

    const handleCloseCopy = () => {
        setOpenCopy(!openCopy);
    };

    const handleOpenCopy = () => {
        setOpenCopy(!openCopy);
        setCopyText(t("profile.username.copy"));
    };

    return (
        <Card variant="outlined" className='dashboard-card identity'>
            <IdentityWaves
                leftColor={(state.currentProfile.theme?.background as any)?.gradient?.left ?? BRAND_LEFT}
                rightColor={(state.currentProfile.theme?.background as any)?.gradient?.right ?? BRAND_RIGHT}
                visible={!loading}
            />
            <CardContent className='dashboard-card-content identity-content'>
                <div className='indentity-header'>
                    {avatarSrc && <Avatar src={avatarSrc} className='identity-avatar' />}
                    <div>
                        <Typography variant="body1" component="p" className='identity-title'>
                            Hey, {username} 👋
                        </Typography>
                        <div className='identity-status-container'>
                            {loading
                                ? <Skeleton variant="circular" width={10} height={10} />
                                : <StatusIndicator status={pending ? STATUS.DEFAULT : profileStatus} />
                            }
                            <Typography variant="body1" className='identity-url'>
                                linkerto.me/<span className='identity-username'>{username}</span>
                            </Typography>
                            <Tooltip
                                open={openCopy}
                                onClose={handleCloseCopy}
                                onOpen={handleOpenCopy}
                                disableFocusListener
                                placement="top"
                                arrow
                                title={copytext}
                            >
                                <IconButton className='identity-copy' onClick={handleCopyLink} disabled={!window.isSecureContext} size="small">
                                    <FaRegCopy />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <div className='indentity-footer'>
                    {loading ? (
                        <Skeleton variant="text" width={140} height={18} />
                    ) : (
                        <Typography variant="body2" className='identity-footer-label'>
                            {profileStatus === STATUS.DRAFT && t('dashboard.main.identity.footer.unsaved')}
                            {(profileStatus === STATUS.NOT_PUBLISHED || profileStatus === STATUS.DEFAULT) && t('dashboard.main.identity.footer.private')}
                            {profileStatus === STATUS.PUBLISHED && t('dashboard.main.identity.footer.public')}
                        </Typography>
                    )}
                    <div className='identity-footer-actions'>
                        {loading ? (
                            <Button color="primary" variant="outlined" size="large" disabled>
                                <div className="btn-text-container"><Skeleton width={80} height={16} /></div>
                            </Button>
                        ) : (
                            <>
                                {profileStatus === STATUS.PUBLISHED && (
                                    <Button
                                        color="primary"
                                        variant="outlined"
                                        size="large"
                                        href={`https://linkerto.me/${username}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('dashboard.main.identity.footer.btn.view')}
                                    </Button>
                                )}
                                {(profileStatus === STATUS.NOT_PUBLISHED || profileStatus === STATUS.DEFAULT) && (
                                    <Tooltip title={isInDeletion ? t('dashboard.main.deletion-alert.guard-tooltip') : ''}>
                                        <span>
                                            <Button color="primary" variant="contained" size="large" onClick={deploy} disabled={pending || isInDeletion}>
                                                {t('dashboard.main.identity.footer.btn.publish')}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {profileStatus === STATUS.DRAFT && (
                                    <Tooltip title={isInDeletion ? t('dashboard.main.deletion-alert.guard-tooltip') : ''}>
                                        <span>
                                            <Button color="primary" variant="contained" size="large" onClick={deploy} disabled={pending || isInDeletion}>
                                                {t('dashboard.main.identity.footer.btn.save')}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default IndentityCard
